(() => {
  const initializedSections = new WeakSet();
  const observedSections = new WeakSet();
  const sectionObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            observer.unobserve(entry.target);
            initializeSection(entry.target);
          });
        },
        { rootMargin: '600px 0px' },
      )
    : null;

  function readCachedBrand(key) {
    try {
      const value = window.sessionStorage.getItem(key);
      if (value === null) return null;
      try {
        const payload = JSON.parse(value);
        return payload && typeof payload === 'object' ? payload : null;
      } catch (_error) {
        const count = Number.parseInt(value, 10);
        return Number.isInteger(count) ? { count } : null;
      }
    } catch (_error) {
      return null;
    }
  }

  function cacheBrand(key, payload) {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(payload));
    } catch (_error) {
      // The section still works when storage is unavailable.
    }
  }

  function initializeSection(section) {
    if (initializedSections.has(section)) return;
    initializedSections.add(section);

    const rail = section.querySelector('[data-brand-rail]');
    const controls = section.querySelector('[data-brand-controls]');
    const previousButton = section.querySelector('[data-brand-previous]');
    const nextButton = section.querySelector('[data-brand-next]');
    const railItems = [...section.querySelectorAll('.jp-latest-brands__item')];
    const items = [...section.querySelectorAll('[data-vendor-count-url]')];
    const queue = [...items];
    let activeRequests = 0;
    const maxConcurrentRequests = 4;

    function updateControls() {
      if (!rail || !previousButton || !nextButton) return;
      const maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
      const railBounds = rail.getBoundingClientRect();
      const firstItemBounds = railItems[0]?.getBoundingClientRect();
      const lastItemBounds = railItems[railItems.length - 1]?.getBoundingClientRect();
      const canScrollPrevious = maxScroll > 2 && firstItemBounds && firstItemBounds.left < railBounds.left - 2;
      const canScrollNext = maxScroll > 2 && lastItemBounds && lastItemBounds.right > railBounds.right + 2;

      previousButton.disabled = !canScrollPrevious;
      previousButton.hidden = !canScrollPrevious;
      nextButton.disabled = !canScrollNext;
      nextButton.hidden = !canScrollNext;
      if (controls) controls.hidden = !canScrollPrevious && !canScrollNext;
    }

    function scrollRail(direction) {
      if (!rail) return;
      const firstItem = rail.querySelector('.jp-latest-brands__item');
      const gap = Number.parseFloat(window.getComputedStyle(rail).columnGap) || 0;
      const distance = firstItem ? firstItem.getBoundingClientRect().width + gap : rail.clientWidth * 0.8;
      rail.scrollBy({
        left: direction * distance,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    }

    previousButton?.addEventListener('click', () => scrollRail(-1));
    nextButton?.addEventListener('click', () => scrollRail(1));
    rail?.addEventListener('scroll', updateControls, { passive: true });
    window.addEventListener('resize', updateControls, { passive: true });
    requestAnimationFrame(() => requestAnimationFrame(updateControls));

    function renderCount(item, count) {
      const card = item.querySelector('[data-brand-card]');
      const badge = item.querySelector('[data-brand-count]');
      const value = item.querySelector('[data-brand-count-value]');
      if (!card || !badge || !value) return;

      value.textContent = String(count);
      badge.classList.remove('is-loading', 'is-error');
      card.setAttribute('aria-label', `${card.dataset.brandName}: ${count} products`);

      if (count === 0) {
        card.classList.add('is-disabled');
        card.setAttribute('aria-disabled', 'true');
        card.setAttribute('tabindex', '-1');
        card.removeAttribute('href');
      }
    }

    function renderPreview(item, payload) {
      if (item.dataset.brandNeedsPreview !== 'true' || !payload.preview_image) return;
      const frame = item.querySelector('.jp-brand-card__logo');
      const fallback = item.querySelector('[data-brand-preview-fallback]');
      if (!frame || !fallback) return;

      const image = document.createElement('img');
      image.className = 'jp-brand-card__image jp-brand-card__image--product';
      image.src = payload.preview_image;
      image.alt = payload.preview_image_alt || item.querySelector('[data-brand-card]')?.dataset.brandName || '';
      image.loading = 'lazy';
      image.decoding = 'async';
      fallback.replaceWith(image);
      item.dataset.brandNeedsPreview = 'false';
    }

    function renderError(item) {
      const badge = item.querySelector('[data-brand-count]');
      const value = item.querySelector('[data-brand-count-value]');
      if (!badge || !value) return;
      value.textContent = '—';
      badge.classList.remove('is-loading');
      badge.classList.add('is-error');
    }

    async function loadCount(item) {
      const vendorUrl = new URL(item.dataset.vendorCountUrl, window.location.origin);
      vendorUrl.searchParams.set('view', 'vendor-count');
      const cacheKey = `jp-brand-count:${vendorUrl.pathname}${vendorUrl.search}`;
      const cachedBrand = readCachedBrand(cacheKey);
      const needsPreview = item.dataset.brandNeedsPreview === 'true';

      if (
        Number.isInteger(cachedBrand?.count) &&
        cachedBrand.count >= 0 &&
        (!needsPreview || cachedBrand.preview_image)
      ) {
        renderCount(item, cachedBrand.count);
        renderPreview(item, cachedBrand);
        return;
      }

      try {
        const response = await fetch(vendorUrl.toString(), {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!response.ok) throw new Error(`Brand count request failed: ${response.status}`);
        const responsePayload = await response.json();
        const count = Number.parseInt(responsePayload.count, 10);
        if (!Number.isInteger(count) || count < 0) throw new Error('Invalid brand count response');
        const payload = {
          count,
          preview_image: typeof responsePayload.preview_image === 'string' ? responsePayload.preview_image : '',
          preview_image_alt: typeof responsePayload.preview_image_alt === 'string' ? responsePayload.preview_image_alt : '',
        };
        cacheBrand(cacheKey, payload);
        renderCount(item, count);
        renderPreview(item, payload);
      } catch (_error) {
        renderError(item);
      }
    }

    function drainQueue() {
      while (activeRequests < maxConcurrentRequests && queue.length > 0) {
        const item = queue.shift();
        activeRequests += 1;
        loadCount(item).finally(() => {
          activeRequests -= 1;
          drainQueue();
        });
      }
    }

    drainQueue();
  }

  function initializeAll(root = document) {
    const sections = [];
    if (root.matches?.('[data-jp-latest-brands]')) sections.push(root);
    root.querySelectorAll?.('[data-jp-latest-brands]').forEach((section) => sections.push(section));

    sections.forEach((section) => {
      if (initializedSections.has(section) || observedSections.has(section)) return;
      if (!sectionObserver || window.Shopify?.designMode) {
        initializeSection(section);
        return;
      }
      observedSections.add(section);
      sectionObserver.observe(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
